import axios from 'axios';
import toast from 'react-hot-toast';

// Create axios instance
// For production (mybt.in), API is at https://api.mybt.in
// For development, defaults to http://127.0.0.1:8000
const getBaseURL = () => {
  // Check if we're in production (live domain)
  if (window.location.hostname === 'mybt.in' || window.location.hostname.includes('mybt.in')) {
    return process.env.REACT_APP_API_BASE_URL || 'https://api.mybt.in';
  }
  // Development or local
  return process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
};

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// --- Token Handling ---
// NOTE: For best security, use HttpOnly cookies for tokens (requires backend support).
// This example uses localStorage for demo purposes only.

let refreshInProgress = false;
let refreshSubscribers = [];

function onRefreshed(token) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
  refreshSubscribers.push(callback);
}

// Request interceptor: Attach access token
api.interceptors.request.use(
  (config) => {
    // If using HttpOnly cookies, skip this step (browser sends cookies automatically)
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor: Handle 401, refresh, and redirect
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and not already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If refresh already in progress, queue this request
      if (refreshInProgress) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((token) => {
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            } else {
              // Show session expired toast and reject
              toast.error('Session expired. Please log in again.');
              reject(error);
            }
          });
        });
      }

      refreshInProgress = true;
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          // Send refresh token in request body, not Authorization header
          const response = await axios.post(
            '/api/auth/refresh',
            { refresh_token: refreshToken },
            {
              baseURL: getBaseURL(),
            }
          );
          const { access_token, refresh_token } = response.data;
          localStorage.setItem('access_token', access_token);
          if (refresh_token) {
            localStorage.setItem('refresh_token', refresh_token);
          }
          api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
          onRefreshed(access_token);
          refreshInProgress = false;
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } else {
          // No refresh token, treat as session expired
          toast.error('Session expired. Please log in again.');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
          refreshInProgress = false;
          onRefreshed(null);
          return Promise.reject(error);
        }
      } catch (refreshError) {
        refreshInProgress = false;
        onRefreshed(null); // Notify all subscribers of failure
        // Remove tokens and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        toast.error('Session expired. Please log in again.');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Centralized error handling for all other errors
    handleAPIError(error); // Show user-friendly message
    return Promise.reject(error);
  }
);

// API endpoints
export const authAPI = {
  login: (credentials) => api.post('/api/auth/login', credentials),
  register: (userData) => api.post('/api/auth/register', userData),
  logout: () => api.post('/api/auth/logout'),
  refresh: () => api.post('/api/auth/refresh'),
  me: () => api.get('/api/profile'),
  changePassword: (passwordData) => api.post('/api/auth/change-password', passwordData),
};

export const userAPI = {
  getAll: (params) => api.get('/api/users', { params }),
  getById: (id) => api.get(`/api/users/${id}`),
  create: (userData) => api.post('/api/users', userData),
  update: (id, userData) => api.put(`/api/users/${id}`, userData),
  delete: (id) => api.delete(`/api/users/${id}`),
  updateProfile: (profileData) => api.put('/api/users/profile', profileData),
  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post('/api/users/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  userSearch: (query) => api.get('/api/users/search', { params: { query } }),
};

export const dashboardAPI = {
  getAdminStats: () => api.get('/api/dashboard/admin'),
  getDistributorStats: () => api.get('/api/dashboard/distributor'),
  getUserStats: () => api.get('/api/dashboard/user'),
  getSalesData: (params) => api.get('/api/dashboard/sales', { params }),
  getPerformanceData: (params) => api.get('/api/dashboard/performance', { params }),
  getActivityLog: (params) => api.get('/api/dashboard/activity', { params }),
};

export const taskAPI = {
  getAll: (params) => api.get('/api/tasks', { params }),
  getById: (id) => api.get(`/api/tasks/${id}`),
  create: (taskData) => api.post('/api/tasks', taskData),
  update: (id, taskData) => api.put(`/api/tasks/${id}`, taskData),
  delete: (id) => api.delete(`/api/tasks/${id}`),
  complete: (id) => api.post(`/api/tasks/${id}/complete`),
  assign: (id, userId) => api.post(`/api/tasks/${id}/assign`, { user_id: userId }),
};

export const reportAPI = {
  generate: (type, params) => api.post(`/api/reports/${type}`, params),
  download: (id) => api.get(`/api/reports/${id}/download`),
  getHistory: (params) => api.get('/api/reports/history', { params }),
};

export const notificationAPI = {
  getAll: (params) => api.get('/api/notifications', { params }),
  markAsRead: (id) => api.put(`/api/notifications/${id}/read`),
  markAllAsRead: () => api.put('/api/notifications/read-all'),
  getUnreadCount: () => api.get('/api/notifications/unread-count'),
};

export const fileAPI = {
  upload: (file, type = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    return api.post('/api/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  download: (id) => api.get(`/api/files/${id}/download`),
  delete: (id) => api.delete(`/api/files/${id}`),
  getByType: (type, params) => api.get(`/api/files/${type}`, { params }),
};

export const auditAPI = {
  getLogs: (params) => api.get('/api/audit/logs', { params }),
  getUserActivity: (userId, params) => api.get(`/api/audit/users/${userId}/activity`, { params }),
  getSystemActivity: (params) => api.get('/api/audit/system', { params }),
};

export const walletAPI = {

  getTotalBalances: (type) => api.get('/api/wallet/total-balances', { params: type ? { type } : {} }),
  getUserWallet: () => api.get('/api/wallets/user'),
  getUserTransactions: () => api.get('/api/wallet/transactions'),
  walletToWalletTransfer: (data) => api.post('/api/wallet/transfer', data),
  getSettingsImages: () => api.get('/api/admin/settings/images'),
  uploadSettingsImage: (formData) => api.post('/api/settings/upload-image', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  
  // Payment Request methods
  getPaymentRequests: (params) => api.get('/api/payment-requests', { params }),
  submitPaymentRequest: (formData) => {
    // Create a new axios instance without default headers for file uploads
    const uploadApi = axios.create({
      baseURL: process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000',
      timeout: 30000,
      headers: {
        'Accept': 'application/json',
        // Don't set Content-Type - let browser set it automatically for FormData
      }
    });
    
    // Add auth token
    const token = localStorage.getItem('access_token');
    if (token) {
      uploadApi.defaults.headers.Authorization = `Bearer ${token}`;
    }
    
    return uploadApi.post('/api/payment-requests', formData);
  },
  getPaymentRequest: (id) => api.get(`/api/payment-requests/${id}`),
  
  // User slip viewing methods
  getUserSlip: (id) => api.get(`/api/payment-requests/${id}/slip`, { responseType: 'blob' }),
  downloadUserSlip: async (id) => {
    const response = await api.get(`/api/payment-requests/${id}/slip`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = `slip_${id}.png`; // Default to PNG, can be dynamic based on content-type
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  
  // Admin payment request methods
  getAdminPaymentRequests: (params) => api.get('/api/admin/payment-requests', { params }),
  adminPaymentAction: (id, data) => api.post(`/api/admin/payment-requests/${id}/action`, data),
  getPaymentSlip: (id) => api.get(`/api/admin/payment-requests/${id}/slip`, { responseType: 'blob' }),
  
  // Statement methods
  getAdminStatements: (params) => api.get('/api/admin/statements', { params }),
};

// Utility functions
export const handleAPIError = (error) => {
  console.error('API Error:', error);
  
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'Authentication required. Please log in.';
      case 403:
        return 'Access denied. You don\'t have permission for this action.';
      case 404:
        return 'Resource not found.';
      case 422:
        return data.message || 'Validation failed. Please check your input.';
      case 429:
        return 'Too many requests. Please try again later.';
      case 500:
        return 'Server error. Please try again later.';
      default:
        return data.error || 'An unexpected error occurred.';
    }
  } else if (error.request) {
    // Network error
    return 'Network error. Please check your connection.';
  } else {
    // Other error
    return error.message || 'An unexpected error occurred.';
  }
};

export const createAPIError = (message, status = 500) => {
  const error = new Error(message);
  error.response = { status, data: { error: message } };
  return error;
};

// Export the api instance for direct use
export default api;